import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageOff, Loader2, Search } from "lucide-react";

import { cn, getFileUrl } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";
import { useLibraryInfinite } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { GenerationReference } from "../utils/ticket-context";

interface GenerationPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (generation: GenerationReference) => void;
}

/**
 * Kullanıcının kendi üretimlerinden birini destek mesajına referans olarak
 * seçtiği diyalog. Genel dosya seçiciden farkı, yalnızca çıktıları (isInput
 * false) listelemesi ve prompt metnini öne çıkarmasıdır — destek ekibinin
 * ihtiyaç duyduğu asıl bağlam prompt'tur.
 */
export const GenerationPickerDialog: React.FC<GenerationPickerDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useLibraryInfinite({
      limit: 18,
      isInput: false,
      search: debouncedSearch || undefined,
      sortBy: "-createdAt",
    });

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.result ?? []) ?? [],
    [data?.pages],
  );

  const handleSelect = (item: (typeof items)[number]) => {
    onSelect({
      id: item._id,
      title: item.title,
      type: item.type,
      fileUrl: item.file.fileUrl,
      prompt: item.prompt,
    });
    onOpenChange(false);
    setSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        open={open}
        cleanupMode="scoped"
        className="flex max-h-[90dvh] w-full max-w-3xl flex-col gap-0 lg:max-w-5xl"
      >
        {/* Başlık + arama — hafif muted yüzey, alt çizgi ile içerikten ayrılır */}
        <div className="shrink-0 border-b border-border/40 bg-muted/[0.15] px-6 pb-4 pt-5">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-lg font-medium text-foreground">{t("support.tools.generationTitle")}</DialogTitle>
            <DialogDescription className="mt-1">
              {t("support.tools.generationDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("support.tools.generationSearch")}
              className="pl-9"
            />
          </div>
        </div>

        {/* Kaydırılabilir içerik */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <ImageOff className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("support.tools.generationEmpty")}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {items.map((item) => {
                  const url = getFileUrl(item.file.fileUrl);
                  return (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => handleSelect(item)}
                      className={cn(
                        "group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left transition-all",
                        "hover:border-indigo-500/60 hover:shadow-md focus:outline-none focus-visible:border-indigo-500",
                      )}
                    >
                      <div className="relative aspect-square w-full overflow-hidden bg-muted">
                        {item.type === "image" && url ? (
                          <img
                            src={url}
                            alt={item.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs uppercase text-muted-foreground">
                            {item.type}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 p-2.5">
                        <span className="truncate text-xs font-medium text-foreground">
                          {item.title}
                        </span>
                        <span className="line-clamp-2 min-h-[28px] text-[11px] leading-snug text-muted-foreground">
                          {item.prompt || t("support.tools.generationNoPrompt")}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {hasNextPage && (
                <div className="flex justify-center pt-5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="dark:border-border dark:bg-muted/50 dark:text-foreground dark:hover:bg-muted"
                  >
                    {isFetchingNextPage && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                    {t("common.loadMore")}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
